import { Button, Card, Descriptions, Empty, Spin, Table, Tag, Timeline, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRiskCard, useRiskMeasures } from '@/entities/risk/api/hooks';
import type { RiskMeasure } from '@/entities/risk/model/riskSchema';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { Status } from '@/entities/user/model/userSchema';
import { measureStatusLabel, riskLevelColors } from '../constants';
import s from './RiskCardDetail.module.scss';

const { Text, Title } = Typography;

function cardStatusLabel(st: Status): string {
  if (st === 'ok') return 'Утверждена';
  if (st === 'info') return 'Согласование';
  return 'Черновик';
}

export function RiskCardDetail() {
  const { cardId } = useParams<{ cardId: string }>();
  const id = Number(cardId);
  const navigate = useNavigate();
  const { data: card, isLoading: cardLoading } = useRiskCard(id);
  const { data: measures, isLoading: measuresLoading } = useRiskMeasures();

  const relatedMeasures = useMemo(() => {
    if (!card) return [];
    return (measures ?? []).filter((m) => m.workplaceId === card.workplaceId);
  }, [card, measures]);

  const measureColumns: ColumnsType<RiskMeasure> = useMemo(
    () => [
      { title: 'Мероприятие', dataIndex: 'title', key: 't' },
      { title: 'РМ', dataIndex: 'workplaceId', key: 'wp', width: 88 },
      { title: 'Ответственный', dataIndex: 'responsible', key: 'r' },
      { title: 'Срок', dataIndex: 'deadline', key: 'd', width: 100 },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 's',
        width: 120,
        render: (st: Status) => <StatusBadge status={st} label={measureStatusLabel[st]} />,
      },
    ],
    [],
  );

  if (!Number.isFinite(id) || id <= 0) {
    return <Empty description="Некорректный идентификатор карты" />;
  }

  return (
    <>
      <Button type="link" className={s.root__back} onClick={() => navigate('/risks')}>
        ← К картам рисков
      </Button>

      <Spin spinning={cardLoading}>
        {!cardLoading && card === null ? (
          <Empty description="Карта не найдена" />
        ) : card ? (
          <>
            <Card className={s['root__header-card']} classNames={{ body: s['root__header-body'] }}>
              <div className={s['root__header-top']}>
                <div>
                  <Title level={5} className={s['root__header-title']}>
                    Карта риска — {card.hazard}
                  </Title>
                  <Text type="secondary" className={s['root__header-sub']}>
                    {card.workplace}
                  </Text>
                </div>
                <StatusBadge status={card.status} label={cardStatusLabel(card.status)} />
              </div>

              <Descriptions column={{ xs: 1, sm: 3 }} size="small" className={s['root__compact-desc']}>
                <Descriptions.Item label="Идентификатор РМ">{card.workplaceId}</Descriptions.Item>
                <Descriptions.Item label="Вероятность (В)">{card.probability}</Descriptions.Item>
                <Descriptions.Item label="Тяжесть (Т)">{card.severity}</Descriptions.Item>
                <Descriptions.Item label="Уровень / балл">
                  <Tag color={riskLevelColors[card.level]}>
                    {card.level} ({card.riskScore})
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Пересмотр">{card.reviewDate ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Источник / норма">{card.source ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Обоснование" span={3}>
                  {card.justification ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Остаточный риск" span={3}>
                  {card.residualRiskNotes ?? '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title={<span className={s['root__timeline-title']}>Хронология</span>}
              className={s['root__timeline-card']}
            >
              <Timeline
                items={card.timeline.map((h, i) => ({
                  key: `${h.date}-${i}`,
                  children: (
                    <>
                      <Text type="secondary" className={s['root__activity-time']}>
                        {h.date}
                      </Text>
                      <div className={s['root__activity-event']}>{h.event}</div>
                    </>
                  ),
                }))}
              />
            </Card>

            <Card
              title={<span className={s['root__timeline-title']}>Мероприятия по этому рабочему месту</span>}
              className={s['root__timeline-card']}
            >
              <Spin spinning={measuresLoading}>
                <Table
                  size="small"
                  dataSource={relatedMeasures}
                  columns={measureColumns}
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: 'Нет записей' }}
                />
              </Spin>
            </Card>
          </>
        ) : null}
      </Spin>
    </>
  );
}
